import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { KeyRound, X } from "lucide-react";

/**
 * Bandeau jaune non-bloquant (dismissible) qui rappelle aux comptes
 * admin-provisionnés (Association/ONG, Syndicat) de changer leur mot de passe
 * dans les 48h après la création.
 *
 * S'affiche uniquement si :
 *  - Le user a `password_set_by_admin === true`
 *  - Le user a été créé il y a moins de 48h
 *  - Le user n'a pas dismissé le bandeau pour cette session
 */
export default function PasswordChangeReminder() {
  const [visible, setVisible] = useState(false);
  const [hoursLeft, setHoursLeft] = useState(48);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!data?.password_set_by_admin) return;
        if (data?.password_changed_at) return; // Déjà changé
        const created = new Date(data.created_at || 0).getTime();
        const elapsedMs = Date.now() - created;
        const elapsedH = elapsedMs / (1000 * 60 * 60);
        if (elapsedH > 48) return; // La fenêtre est passée
        // Ne pas ré-afficher si dismissé cette session
        const dismissed = sessionStorage.getItem("pwd-reminder-dismissed");
        if (dismissed) return;
        setHoursLeft(Math.max(1, Math.round(48 - elapsedH)));
        setVisible(true);
      } catch { /* silent */ }
    })();
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    sessionStorage.setItem("pwd-reminder-dismissed", "1");
    setVisible(false);
  };

  return (
    <div
      data-testid="pwd-reminder-banner"
      className="bg-[#FFFBEB] border-b-2 border-[#FFD700] px-6 py-3 flex items-center justify-between gap-3"
    >
      <div className="flex items-center gap-3 text-sm">
        <div className="p-2 bg-[#FFD700] rounded-full flex-shrink-0">
          <KeyRound className="h-4 w-4 text-[#0A0A0A]" />
        </div>
        <div>
          <div className="font-semibold text-[#0A0A0A]">
            Pensez à changer votre mot de passe initial
          </div>
          <div className="text-xs text-[#78350F] mt-0.5">
            Ce compte a été créé par l&apos;équipe PAG. Nous vous recommandons de personnaliser votre mot de passe dans les <b>{hoursLeft}h</b> restantes.
            <span className="opacity-70"> (rappel non bloquant)</span>
          </div>
        </div>
      </div>
      <button
        onClick={dismiss}
        data-testid="pwd-reminder-dismiss"
        className="p-1.5 hover:bg-black/5 rounded transition-colors flex-shrink-0"
        aria-label="Ignorer"
      >
        <X className="h-4 w-4 text-[#495057]" />
      </button>
    </div>
  );
}
